import { useMemo, useState } from 'react';
import { Wifi, WifiOff, ArrowUp, ArrowDown, Clock, Plug } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface WebSocketConnection {
  url: string;
  entries: HAREntry[];
  status: 'connecting' | 'open' | 'closed' | 'error';
  messagesSent: number;
  messagesReceived: number;
  totalBytesSent: number;
  totalBytesReceived: number;
  duration: number;
  startTime: number;
  endTime?: number;
}

export function WebSocketSupport() {
  const { filteredEntries, selectEntry } = useHARStore();
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);

  const websocketConnections = useMemo(() => {
    const connections: Record<string, WebSocketConnection> = {};
    const websocketEntries = filteredEntries.filter(entry => {
      // WebSocket URLs start with ws:// or wss://
      return entry.request.url.startsWith('ws://') || entry.request.url.startsWith('wss://');
    });

    websocketEntries.forEach(entry => {
      const url = entry.request.url;
      
      if (!connections[url]) {
        connections[url] = {
          url,
          entries: [],
          status: 'connecting',
          messagesSent: 0,
          messagesReceived: 0,
          totalBytesSent: 0,
          totalBytesReceived: 0,
          duration: 0,
          startTime: new Date(entry.startedDateTime).getTime(),
        };
      }

      connections[url].entries.push(entry);
      
      // Update connection status based on response
      const upgradeHeader = entry.response.headers.find(
        h => h.name.toLowerCase() === 'upgrade'
      );
      
      if (upgradeHeader?.value?.toLowerCase() === 'websocket') {
        connections[url].status = 'open';
      } else if (entry.response.status >= 400) {
        connections[url].status = 'error';
      }

      // Update timing
      const entryTime = new Date(entry.startedDateTime).getTime();
      connections[url].endTime = Math.max(
        connections[url].endTime || 0,
        entryTime + entry.time
      );
    });

    // Calculate duration and message counts for each connection
    Object.values(connections).forEach(conn => {
      if (conn.endTime) {
        conn.duration = conn.endTime - conn.startTime;
      }
      
      // Count messages (each entry after the initial handshake is a message)
      // The first entry is typically the upgrade request
      const messages = conn.entries.slice(1);
      conn.messagesReceived = messages.filter(e => e.response.content.size > 0).length;
      conn.messagesSent = messages.filter(e => e.request.bodySize > 0).length;
      
      // Calculate bytes
      conn.totalBytesReceived = messages.reduce((sum, e) => sum + (e.response.content.size || 0), 0);
      conn.totalBytesSent = messages.reduce((sum, e) => sum + (e.request.bodySize || 0), 0);
    });

    return Object.values(connections);
  }, [filteredEntries]);

  const messages = useMemo(() => {
    if (!selectedConnection) return [];
    
    const connection = websocketConnections.find(c => c.url === selectedConnection);
    if (!connection) return [];

    return connection.entries.slice(1).map(entry => ({
      entry,
      direction: entry.request.bodySize > 0 ? 'sent' as const : 'received' as const,
      type: determineMessageType(entry),
      size: entry.request.bodySize > 0 ? entry.request.bodySize : (entry.response.content.size || 0),
      time: entry.time,
    }));
  }, [selectedConnection, websocketConnections]);

  if (websocketConnections.length === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <WifiOff className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No WebSocket connections found</h3>
        <p className="text-xs text-slate-500">This HAR file doesn't contain any WebSocket traffic.</p>
      </div>
    );
  }

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Connections List */}
        <div className={cn(
          "flex-shrink-0 overflow-y-auto custom-scrollbar space-y-2",
          selectedConnection ? "w-1/3" : "w-full"
        )}>
          {websocketConnections.map(conn => (
            <button
              key={conn.url}
              onClick={() => setSelectedConnection(selectedConnection === conn.url ? null : conn.url)}
              className={cn(
                'w-full text-left p-3 rounded-lg border transition-all',
                selectedConnection === conn.url
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {conn.status === 'open' && <Wifi className="w-4 h-4 text-green-500" />}
                  {conn.status === 'error' && <WifiOff className="w-4 h-4 text-red-500" />}
                  {conn.status === 'connecting' && <Plug className="w-4 h-4 text-yellow-500" />}
                  <span className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">
                    {getFileName(conn.url)}
                  </span>
                </div>
                <span className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded uppercase font-bold',
                  conn.status === 'open' && 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300',
                  conn.status === 'error' && 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
                  conn.status === 'connecting' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300'
                )}>
                  {conn.status}
                </span>
              </div>
              
              <div className="text-[10px] text-slate-500 truncate mb-2">
                {conn.url}
              </div>

              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                  <ArrowUp className="w-3 h-3" />
                  {conn.messagesSent}
                </span>
                <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                  <ArrowDown className="w-3 h-3" />
                  {conn.messagesReceived}
                </span>
                <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                  <Clock className="w-3 h-3" />
                  {formatTime(conn.duration)}
                </span>
              </div>

              <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
                <span>{formatBytes(conn.totalBytesSent)} sent</span>
                <span>{formatBytes(conn.totalBytesReceived)} received</span>
              </div>
            </button>
          ))}
        </div>

        {/* Messages Panel */}
        {selectedConnection && (
          <div className="flex-1 flex flex-col border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Messages
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-xs text-slate-500">
                  No messages captured for this connection
                </div>
              ) : (
                messages.map((msg, i) => (
                  <button
                    key={i}
                    onClick={() => selectEntry(msg.entry)}
                    className="w-full text-left p-2 rounded bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {msg.direction === 'sent' ? (
                          <ArrowUp className="w-3 h-3 text-blue-500" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-green-500" />
                        )}
                        <span className={cn(
                          'text-[10px] px-1.5 py-0.5 rounded uppercase font-bold',
                          msg.direction === 'sent' 
                            ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300'
                            : 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300'
                        )}>
                          {msg.direction}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {msg.type}
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500">
                        {formatTime(msg.time)}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-600 dark:text-slate-400">
                      {formatBytes(msg.size)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function determineMessageType(entry: HAREntry): 'text' | 'binary' | 'ping' | 'pong' | 'close' {
  const contentType = entry.response.content.mimeType?.toLowerCase() || '';
  
  if (contentType.includes('text') || contentType.includes('json')) {
    return 'text';
  }
  if (contentType.includes('binary') || contentType.includes('octet-stream')) {
    return 'binary';
  }
  
  // Check for close frame (status code 1000 or similar)
  if (entry.response.status === 0 || entry.response.status === 1000) {
    return 'close';
  }
  
  return 'text';
}
